import { InputAdornment, Stack, TextField, Tooltip, Typography } from '@mui/material'
import Slider from '@mui/material/Slider'
import React, { ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { formatMoney } from '../../../packages/utils/numberFormatters'
import { FormPopoverField } from './FormPopoverField'

const getTextFieldValue = ({
    allSelectedMessage,
    valueLimits,
    values,
    unit,
    locale,
}: {
    allSelectedMessage: string
    valueLimits: number[]
    values: number[]
    unit: string
    locale?: string
}) => {
    switch (true) {
        case values[0] === 0 && values[1] == valueLimits[1]:
            return allSelectedMessage
        case values[1] === valueLimits[1]:
            return `> ${formatMoney(values[0], { locale, minDecimals: 0, maxDecimals: 2 })} ${unit}`
        default:
            return `${values.map((value) => formatMoney(value, { locale, minDecimals: 0, maxDecimals: 2 })).join(' - ')} ${unit}`
    }
}

interface MinimumDistanceSliderProps {
    allSelectedMessage: string
    label: string
    value: number[]
    includeGreaterValues: boolean
    valueLimits: number[]
    onChange: (val: number[], isIncludeGreaterValues: boolean) => void
    unit: string
    includeInputFields?: boolean
}

export const MinimumDistanceSlider = ({
    allSelectedMessage,
    label,
    value,
    includeGreaterValues,
    valueLimits,
    onChange,
    unit,
    includeInputFields = false,
}: MinimumDistanceSliderProps) => {
    const { i18n } = useTranslation()
    const locale = i18n.language

    const [minValue, maxValue] = valueLimits
    const [sliderValue, setSliderValue] = useState<number[]>([value[0], value[1]])
    const [inputValue, setInputValue] = useState<string[]>([value[0].toString(), value[1].toString()])

    const isIncludeGreaterValues = includeGreaterValues && value[1] === maxValue

    const handleSliderChange = (event, newValue: number | number[]) => {
        setSliderValue(newValue as number[])
    }

    const handleSliderUpdate = (event, newValue: number | number[]) => {
        const castNewValue = newValue as number[]
        setSliderValue(castNewValue)
        onChange(castNewValue, includeGreaterValues && castNewValue[1] === maxValue)
        if (includeInputFields) {
            setInputValue([castNewValue[0].toString(), castNewValue[1].toString()])
        }
    }

    const handleInputValues = (inputMinValue: number, inputMaxValue: number) => {
        setInputValue([
            formatMoney(inputMinValue, { locale, minDecimals: 0, maxDecimals: 2 }),
            formatMoney(inputMaxValue, { locale, minDecimals: 0, maxDecimals: 2 }),
        ])
    }

    const handleInputMinValue = () => {
        const inputMinValue = parseFloat(inputValue[0].replace(',', '.'))

        if (inputMinValue < minValue) {
            handleSliderUpdate('', [minValue, sliderValue[1]])
            handleInputValues(minValue, sliderValue[1])
        } else if (inputMinValue > sliderValue[1]) {
            handleSliderUpdate('', [sliderValue[1], sliderValue[1]])
            handleInputValues(sliderValue[1], sliderValue[1])
        } else {
            handleSliderUpdate('', [inputMinValue, sliderValue[1]])
            handleInputValues(inputMinValue, sliderValue[1])
        }
    }

    const handleInputMaxValue = () => {
        const inputMaxValue = parseFloat(inputValue[1].replace(',', '.'))

        if (inputMaxValue > maxValue) {
            handleSliderUpdate('', [sliderValue[0], maxValue])
            handleInputValues(sliderValue[0], maxValue)
        } else if (inputMaxValue < sliderValue[0]) {
            handleSliderUpdate('', [sliderValue[0], sliderValue[0]])
            handleInputValues(sliderValue[0], sliderValue[0])
        } else {
            handleSliderUpdate('', [sliderValue[0], inputMaxValue])
            handleInputValues(sliderValue[0], inputMaxValue)
        }
    }

    const valuetext = (value: number) => `${value}%`

    const nrDecimals = maxValue <= 10 ? 2 : 0

    // Custom ValueLabel component
    const ValueLabelComponent = (props: { children: ReactElement; value: number }) => {
        const { children, value } = props
        const { i18n } = useTranslation()
        const locale = i18n.language

        return (
            <Tooltip
                enterTouchDelay={0}
                placement='top'
                arrow
                title={formatMoney(value, {
                    locale,
                    decimals: nrDecimals,
                })}
            >
                {children}
            </Tooltip>
        )
    }

    const textFieldValue = getTextFieldValue({ allSelectedMessage, valueLimits, values: value, unit, locale })

    return (
        <FormPopoverField label={label} textFieldValue={textFieldValue}>
            <Stack spacing={2}>
                {!includeInputFields ? (
                    <Stack direction='row'>
                        <Typography variant='body1' gutterBottom>
                            {`${formatMoney(value[0], { locale, decimals: nrDecimals })} - ${
                                isIncludeGreaterValues ? '>' : ''
                            } ${formatMoney(value[1], {
                                locale,
                                decimals: nrDecimals,
                            })} ${unit}`}
                        </Typography>
                    </Stack>
                ) : null}
                {includeInputFields ? (
                    <Stack
                        direction='row'
                        sx={{
                            justifyContent: 'space-between',
                        }}
                    >
                        <TextField
                            label='Min'
                            size='small'
                            value={inputValue[0]}
                            onChange={(event) => {
                                setInputValue([event.target.value, inputValue[1]])
                            }}
                            onBlur={() => handleInputMinValue()}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    handleInputMinValue()
                                }
                            }}
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment
                                            position='end'
                                            sx={{ '& p': { lineHeight: 0, color: '#000000de' } }}
                                        >
                                            MSEK
                                        </InputAdornment>
                                    ),
                                },
                            }}
                            sx={{ width: '120px' }}
                        />
                        <TextField
                            label='Max'
                            size='small'
                            value={inputValue[1]}
                            onChange={(event) => {
                                setInputValue([inputValue[0], event.target.value])
                            }}
                            onBlur={() => handleInputMaxValue()}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    handleInputMaxValue()
                                }
                            }}
                            slotProps={{
                                input: {
                                    startAdornment:
                                        maxValue === sliderValue[1] ? (
                                            <InputAdornment
                                                position='start'
                                                sx={{
                                                    marginRight: '0px',
                                                    '& p': { marginBottom: '1px', color: '#000000de' },
                                                }}
                                            >
                                                {'>'}
                                            </InputAdornment>
                                        ) : null,
                                    endAdornment: (
                                        <InputAdornment
                                            position='end'
                                            sx={{ '& p': { lineHeight: 0, color: '#000000de' } }}
                                        >
                                            MSEK
                                        </InputAdornment>
                                    ),
                                },
                            }}
                            sx={{ width: '120px' }}
                        />
                    </Stack>
                ) : null}
                <Slider
                    sx={{ width: '300px' }}
                    getAriaLabel={() => 'Minimum distance'}
                    value={sliderValue}
                    min={minValue}
                    max={maxValue}
                    onChange={handleSliderChange}
                    onChangeCommitted={handleSliderUpdate}
                    valueLabelDisplay='auto'
                    getAriaValueText={valuetext}
                    disableSwap
                    step={1}
                    slots={{
                        valueLabel: ValueLabelComponent,
                    }}
                />
            </Stack>
        </FormPopoverField>
    )
}
